import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY = {
  project_id: "", name: "", address: "", map_link: "", meeting_point: "", meeting_time: "",
  manager_id: "", contact: "", parking: "", entry_method: "", cautions: "", safety_notes: "",
  work_hours: "", status: "有効",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminSites() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterProject, setFilterProject] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/sites", { params: filterProject ? { project_id: filterProject } : {} }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProject]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/sites/${editing.id}`, form);
      else await api.post("/admin/sites", form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`「${s.name}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/sites/${s.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div data-testid="admin-sites-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">現場の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">SITES — 1案件に複数現場を登録できます</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="sites-filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての案件</option>
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
          <button data-testid="site-add-button" onClick={() => { setForm({ ...EMPTY, project_id: filterProject }); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            現場を登録
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">現場名称</th>
              <th className="px-4 py-3">案件</th>
              <th className="px-4 py-3">住所</th>
              <th className="px-4 py-3">集合</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`site-row-${s.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">{s.name}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{projectName(s.project_id)}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{s.address}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{s.meeting_time} {s.meeting_point}</td>
                <td className="px-4 py-3 text-xs">{s.status === "有効" ? <span className="text-green-400">有効</span> : <span className="text-gray-500">{s.status}</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`site-edit-${s.id}`} onClick={() => { setForm({ ...EMPTY, ...s }); setEditing(s); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`site-delete-${s.id}`} onClick={() => remove(s)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">現場が登録されていません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="site-form" onSubmit={save} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "現場を編集" : "現場を登録"}</h2>
              <button type="button" data-testid="site-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="工事案件 *">
                <select data-testid="site-form-project" required value={form.project_id} onChange={set("project_id")} className={inputCls}>
                  <option value="">選択してください</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="現場名称 *"><input data-testid="site-form-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="第1現場" /></Field>
              <Field label="現場住所"><input data-testid="site-form-address" value={form.address} onChange={set("address")} className={inputCls} /></Field>
              <Field label="地図リンク"><input value={form.map_link} onChange={set("map_link")} className={inputCls} placeholder="https://maps.google.com/..." /></Field>
              <Field label="集合場所"><input value={form.meeting_point} onChange={set("meeting_point")} className={inputCls} /></Field>
              <Field label="集合時刻"><input value={form.meeting_time} onChange={set("meeting_time")} className={inputCls} placeholder="8:00" /></Field>
              <Field label="現場責任者">
                <select value={form.manager_id} onChange={set("manager_id")} className={inputCls}>
                  <option value="">—</option>
                  {staff.filter((s) => s.is_active).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="連絡先"><input value={form.contact} onChange={set("contact")} className={inputCls} /></Field>
              <Field label="駐車場所"><input value={form.parking} onChange={set("parking")} className={inputCls} /></Field>
              <Field label="作業可能時間帯"><input value={form.work_hours} onChange={set("work_hours")} className={inputCls} placeholder="8:00-17:00" /></Field>
            </div>
            <Field label="入場方法"><input value={form.entry_method} onChange={set("entry_method")} className={inputCls} /></Field>
            <Field label="現場注意事項"><textarea rows={2} value={form.cautions} onChange={set("cautions")} className={`resize-y ${inputCls}`} /></Field>
            <Field label="安全上の注意事項"><textarea rows={2} value={form.safety_notes} onChange={set("safety_notes")} className={`resize-y ${inputCls}`} /></Field>
            <Field label="状態">
              <select value={form.status} onChange={set("status")} className={inputCls}>
                <option>有効</option><option>休止</option><option>完了</option>
              </select>
            </Field>
            <button data-testid="site-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
